import selectImage from '../actions/selectImage';
import type { EditorViewState } from '../index';
import {
    IEditor,
    EditorPlugin,
    PluginEvent,
    PluginEventType,
    PluginKeyboardEvent,
    PositionType,
} from 'roosterjs-editor-types';
import getSelectedImage from '../utils/getSelectedImage';

const DELETE_KEYCODE = 46;
const BACKSPACE_KEYCODE = 8;
const ANCHOR_TAG = 'a';

export default class SelectImagePlugin implements EditorPlugin {
    private editor: IEditor;

    constructor(private viewState: EditorViewState) {}

    public getName() {
        return 'SelectImage';
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;

        // Reset selected image to avoid any dirty data (it can happen during popout)
        selectImage(this.viewState, null /*image*/);

        // disable the native image resizing in the browser
        this.editor.getDocument().execCommand('enableObjectResizing', false, 'false');
        this.editor.addContentEditFeature({
            keys: [DELETE_KEYCODE, BACKSPACE_KEYCODE],
            shouldHandleEvent: () => !!getSelectedImage(this.viewState),
            handleEvent: (event: PluginKeyboardEvent) => {
                event.rawEvent.stopPropagation();
                event.rawEvent.preventDefault();
                this.editor.addUndoSnapshot(() => {
                    this.editor.deleteNode(getSelectedImage(this.viewState));
                    this.selectImage(null /*image*/);
                }, 'DeleteImage');
            },
        });
    }

    public dispose(): void {
        this.editor = null;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.MouseUp && event.rawEvent.button == 0) {
            const domElement = <Element>event.rawEvent.target;
            this.selectImage(domElement?.nodeName == 'IMG' ? <HTMLImageElement>domElement : null);
        } else if (event.eventType === PluginEventType.KeyDown) {
            this.selectImage(null /*image*/);
        }
    }

    private selectImage(image: HTMLImageElement) {
        // Need to deselect image first to trigger react render the component.
        // From what I see, directly replace to another DOM node in store won't trigger rerender.
        selectImage(this.viewState, null /*image*/);

        if (image?.parentElement?.tagName.toLowerCase() !== ANCHOR_TAG) {
            selectImage(this.viewState, image);
            this.editor.focus();
            this.editor.select(image, PositionType.After);
        }
    }
}
